import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { defaultTravelAdditionalInfoValues } from "@/D2C/features/travel-quote/redux/travel-quote/travelQuoteSlice";
import {
  ConfirmationCheckbox,
  CountrySelect,
  RadioInput,
  SelectInput,
  TextInput,
} from "@/shared/components";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import type { StructuredDocumentData } from "@/shared/types/documentuploadtype";
import { formatEmiratesId } from "@/shared/utils/emiratesId";
import { createTravelAdditionalInfoSchema } from "@/shared/validation/travelQuoteSchemas";

import type {
  TravelAdditionalInfoTravellerValues,
  TravelAdditionalInfoValues,
  TravelDetailsFormValues,
  TravelUploadedDocumentMap,
} from "../../redux/type";
import {
  TRAVEL_BENEFICIARY_RELATION_OPTIONS,
  TRAVEL_GENDER_OPTIONS,
  TRAVEL_SOURCE_OF_FUND_OPTIONS,
} from "../../utils/travelAdditionalInfo";
import {
  getTravelAdditionalInfoOcrDefaults,
  mergeTravelAdditionalInfoOcrDefaults,
} from "../../utils/travelAdditionalInfoOcr";
import { CommonAccordion } from "./TravellerAccordion";

type TravelAdditionalInfoStepExtraProps = {
  isReadOnly?: boolean;
  ocrStructuredData?: StructuredDocumentData;
  ocrDocuments?: TravelUploadedDocumentMap;
  travelDetails?: TravelDetailsFormValues;
  onResidentConfirmChange?: (checked: boolean) => boolean | Promise<boolean> | void;
  showPersonalInformationTitle?: boolean;
};

function getTravellerLabel(index: number, traveller?: { name?: string; dob?: string }) {
  const title = index === 0 ? "Primary Traveller" : `Traveller ${index + 1}`;
  const details = [traveller?.name, traveller?.dob ? `DOB: ${traveller.dob}` : ""]
    .filter(Boolean)
    .join(" | ");

  return details ? `${title} (${details})` : title;
}

function createEmptyAdditionalInfo(): TravelAdditionalInfoTravellerValues {
  const { travellers: _travellers, ...values } = defaultTravelAdditionalInfoValues;

  return { ...values };
}

function getTravellerDocumentPrefix(index: number) {
  return `traveller_${index}_`;
}

function mergeTravellerStructuredData(
  documents: TravelUploadedDocumentMap | undefined,
  travellerIndex: number
) {
  if (travellerIndex === 0) {
    return undefined;
  }

  const prefix = getTravellerDocumentPrefix(travellerIndex);

  return Object.entries(documents || {}).reduce<StructuredDocumentData>((data, [key, document]) => {
    if (!key.startsWith(prefix)) {
      return data;
    }

    return {
      ...data,
      ...(document?.structuredData || {}),
    };
  }, {});
}

function mergeTravellerDefaults(
  currentValues: TravelAdditionalInfoTravellerValues | undefined,
  ocrDefaults: Partial<TravelAdditionalInfoTravellerValues>
) {
  return mergeTravelAdditionalInfoOcrDefaults(
    {
      ...createEmptyAdditionalInfo(),
      ...currentValues,
    },
    ocrDefaults
  ) as TravelAdditionalInfoTravellerValues;
}

export const TravelAdditionalInfoStep = forwardRef<
  StepFormHandle<TravelAdditionalInfoValues>,
  StepFormComponentProps<TravelAdditionalInfoValues, TravelAdditionalInfoStepExtraProps>
>(function TravelAdditionalInfoStep(
  {
    defaultValues,
    isReadOnly = false,
    ocrStructuredData,
    ocrDocuments,
    travelDetails,
    onResidentConfirmChange,
    showPersonalInformationTitle = true,
  },
  ref
) {
  const [openTravellerIndex, setOpenTravellerIndex] = useState(0);
  const travellers = useMemo(
    () => (travelDetails?.travellers?.length ? travelDetails.travellers : [{ name: "", dob: "" }]),
    [travelDetails?.travellers]
  );
  const hasMultipleTravellers = travellers.length > 1;
  const ocrDefaults = useMemo(
    () => getTravelAdditionalInfoOcrDefaults(ocrStructuredData),
    [ocrStructuredData]
  );
  const additionalTravellerOcrDefaults = useMemo(
    () =>
      travellers.slice(1).map((_, offset) => {
        const structuredData = mergeTravellerStructuredData(ocrDocuments, offset + 1);

        return getTravelAdditionalInfoOcrDefaults(structuredData);
      }),
    [ocrDocuments, travellers]
  );
  const coverageLabel = travelDetails?.coverageLabel || travelDetails?.coverage || "";
  const showEmiratesId = coverageLabel !== "UAE Inbound";
  const additionalInfoSchema = useMemo(
    () =>
      createTravelAdditionalInfoSchema({
        requireEmiratesId: showEmiratesId,
        travellerDob: travelDetails?.travellers?.[0]?.dob || "",
      }),
    [showEmiratesId, travelDetails?.travellers]
  );

  const mergedDefaultValues = useMemo(() => {
    const mergedValues = {
      ...defaultTravelAdditionalInfoValues,
      ...defaultValues,
    };
    const additionalTravellers = travellers
      .slice(1)
      .map((_, index) =>
        mergeTravellerDefaults(
          defaultValues?.travellers?.[index],
          additionalTravellerOcrDefaults[index] || {}
        )
      );

    return {
      ...mergeTravelAdditionalInfoOcrDefaults(mergedValues, ocrDefaults),
      travellers: additionalTravellers,
    };
  }, [additionalTravellerOcrDefaults, defaultValues, ocrDefaults, travellers]);

  const form = useForm<TravelAdditionalInfoValues>({
    resolver: zodResolver(additionalInfoSchema) as any,
    defaultValues: mergedDefaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    form.reset(mergedDefaultValues);
  }, [form, mergedDefaultValues]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (isReadOnly) {
        return true;
      }
      form.setValue("emiratesId", formatEmiratesId(form.getValues("emiratesId")), {
        shouldDirty: true,
        shouldValidate: true,
      });

      const isValid = await form.trigger(undefined, {
        shouldFocus: true,
      });

      if (!isValid) {
        const errors = form.formState.errors;
        const firstAdditionalTravellerWithError = Array.isArray(errors.travellers)
          ? errors.travellers.findIndex(Boolean)
          : -1;

        setOpenTravellerIndex(
          firstAdditionalTravellerWithError >= 0 ? firstAdditionalTravellerWithError + 1 : 0
        );
      }

      return isValid;
    },
    getValues: () => form.getValues(),
  }));
  const renderTravellerFields = (prefix = "") => {
    const name = (fieldName: keyof TravelAdditionalInfoTravellerValues) =>
      prefix ? (`${prefix}.${fieldName}` as const) : fieldName;

    return (
      <>
        <section className="space-y-4">
          {showPersonalInformationTitle ? (
            <h2 className="text-xl font-semibold text-theme-primary">Personal Information</h2>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <RadioInput
              control={form.control}
              name={name("gender")}
              label="Gender"
              options={[...TRAVEL_GENDER_OPTIONS]}
              required
            />

            <CountrySelect
              control={form.control}
              name={name("nationality")}
              label="Nationality"
              placeholder="Select country"
              required
            />

            <TextInput
              control={form.control}
              name={name("passportNumber")}
              label="Passport Number"
              placeholder="e.g. Z1234567"
              required
            />

            <TextInput
              control={form.control}
              name={name("cityOfResidence")}
              label="City of Residence"
              placeholder="e.g. Dubai"
              required
            />

            <TextInput
              control={form.control}
              name={name("address")}
              label="Address"
              placeholder="Enter address"
              className={showEmiratesId ? "" : "sm:col-span-2"}
              required
            />

            {showEmiratesId ? (
              <TextInput
                control={form.control}
                name={name("emiratesId")}
                label="Emirates ID"
                placeholder="784-XXXX-XXXXXXX-X"
                formatValueOnBlur={formatEmiratesId}
                inputMode="numeric"
                maxLength={18}
                required
              />
            ) : null}

            <SelectInput
              control={form.control}
              name={name("sourceOfFund")}
              label="Source of Fund"
              placeholder="Select option"
              options={[...TRAVEL_SOURCE_OF_FUND_OPTIONS]}
              required
            />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-theme-primary">Beneficiary Details</h2>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <TextInput
              control={form.control}
              name={name("beneficiaryName")}
              label="Beneficiary Name"
              placeholder="e.g. John Doe"
              required
            />

            <RadioInput
              control={form.control}
              name={name("beneficiaryRelation")}
              label="Relation"
              options={[...TRAVEL_BENEFICIARY_RELATION_OPTIONS]}
              required
            />
          </div>
        </section>
      </>
    );
  };

  const renderAdditionalTravellerFields = (prefix: string) => {
    const name = (fieldName: keyof TravelAdditionalInfoTravellerValues) =>
      `${prefix}.${fieldName}` as const;

    return (
      <section className="space-y-4">
        {showPersonalInformationTitle ? (
          <h2 className="text-xl font-semibold text-theme-primary">Personal Information</h2>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <RadioInput
            control={form.control}
            name={name("gender")}
            label="Gender"
            options={[...TRAVEL_GENDER_OPTIONS]}
            required
          />

          <CountrySelect
            control={form.control}
            name={name("nationality")}
            label="Nationality"
            placeholder="Select country"
            required
          />

          <TextInput
            control={form.control}
            name={name("passportNumber")}
            label="Passport Number"
            placeholder="e.g. Z1234567"
            required
          />
        </div>
      </section>
    );
  };

  return (
    <div className="space-y-5">
      {hasMultipleTravellers ? (
        travellers.map((traveller, index) => {
          const isOpen = openTravellerIndex === index;
          const prefix = index === 0 ? "" : `travellers.${index - 1}`;

          return (
            <CommonAccordion
              key={`${index}-${traveller?.dob || ""}`}
              title={getTravellerLabel(index, traveller)}
              isOpen={isOpen}
              onToggle={() => setOpenTravellerIndex(isOpen ? -1 : index)}
            >
              <fieldset disabled={isReadOnly} className="contents">
                <div className="space-y-8">
                  {index === 0
                    ? renderTravellerFields(prefix)
                    : renderAdditionalTravellerFields(prefix)}
                </div>
              </fieldset>
            </CommonAccordion>
          );
        })
      ) : (
        <fieldset disabled={isReadOnly} className="contents">
          <div className="space-y-8">{renderTravellerFields()}</div>
        </fieldset>
      )}

      <fieldset disabled={isReadOnly} className="contents">
        <Controller
          control={form.control}
          name="uaeResidentConfirm"
          render={({ field, fieldState }) => (
            <ConfirmationCheckbox
              checked={field.value === true}
              disabled={isReadOnly}
              error={fieldState.error?.message}
              onChange={async (checked) => {
                if (checked) {
                  field.onChange(true);
                  const isValid = await form.trigger(undefined, { shouldFocus: true });
                  if (!isValid) {
                    field.onChange(false);
                    return;
                  }
                } else {
                  field.onChange(false);
                }

                queueMicrotask(async () => {
                  const accepted = await onResidentConfirmChange?.(checked);
                  if (checked && accepted === false) {
                    field.onChange(false);
                    void form.trigger("uaeResidentConfirm");
                  }
                });
              }}
            >
              I confirm that I am a UAE resident and have validated the above information.
            </ConfirmationCheckbox>
          )}
        />
      </fieldset>
    </div>
  );
});

export default TravelAdditionalInfoStep;
